import React, {
    forwardRef,
    memo,
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DatePicker, Icon, Popover, Text, TimePicker, useTheme } from '@Sage/hoi-poi-ui';
import { getVar } from '@Sage/ssm-utils';

import { formatDate, getDateFormatPlaceholder } from 'utils/dates';
import { getLiteral } from 'utils/getLiteral';

import {
    clampCustomRangeToSendAt,
    cloneCustomRangeState,
    formatCustomRangeBadgeLabel,
    fromTimePickerDisplayValue,
    maxLocalCalendarDayDate,
    startOfLocalDay,
    toTimePickerDisplayValue,
} from './usePerformanceOverTime';

/** Same interval as `DateTime` field (`apps/ssm-web4/.../Fields/DateTime.jsx`). */
const TIME_INTERVAL_MINUTES = 5;

const CUSTOM_RANGE_POPOVER_OVERLAY_STYLE = {
    maxWidth: 'min(560px, calc(100vw - 32px))',
    minWidth: 320,
};

const CUSTOM_RANGE_POPOVER_INNER_STYLE = {
    boxSizing: 'border-box',
    maxWidth: '100%',
    width: '100%',
};

const EMPTY_CUSTOM_DRAFT = {
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
};

function isSameCalendarDay(dateA, dateB) {
    if (!dateA || !dateB) return false;
    return (
        dateA.getFullYear() === dateB.getFullYear() &&
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getDate() === dateB.getDate()
    );
}

function combineLocalDateAndTime(date, time) {
    if (!date || !time) return null;
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    return combined;
}

/** `TimePicker` minTime / maxTime expect `HH:mm:ss`. */
function formatHhMmSsForTimePicker(time) {
    if (!time) return undefined;
    const h = String(time.getHours()).padStart(2, '0');
    const m = String(time.getMinutes()).padStart(2, '0');
    const s = String(time.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
}

const PerformanceOverTimeCustomRangePopover = memo(
    forwardRef(function PerformanceOverTimeCustomRangePopover(
        { campaignSendAt, customRange, onApplyCustomRange, preset, presets },
        ref,
    ) {
        const theme = useTheme();
        const lang = useSelector((state) => state.config?.userData?.langISOInterface);
        const userDateFormat = getDateFormatPlaceholder();
        const [popoverVisible, setPopoverVisible] = useState(false);
        const [draftCustomRange, setDraftCustomRange] = useState(EMPTY_CUSTOM_DRAFT);
        const draftCustomRangeRef = useRef(EMPTY_CUSTOM_DRAFT);
        const skipCommitOnNextCloseRef = useRef(false);
        const wasPopoverOpenRef = useRef(false);

        useLayoutEffect(() => {
            if (popoverVisible) {
                wasPopoverOpenRef.current = true;
                return;
            }
            if (!wasPopoverOpenRef.current) {
                return;
            }
            wasPopoverOpenRef.current = false;
            if (skipCommitOnNextCloseRef.current) {
                skipCommitOnNextCloseRef.current = false;
                return;
            }
            onApplyCustomRange(draftCustomRangeRef.current);
        }, [onApplyCustomRange, popoverVisible]);

        useImperativeHandle(
            ref,
            () => ({
                closeWithoutCommit: () => {
                    skipCommitOnNextCloseRef.current = true;
                    setPopoverVisible(false);
                },
            }),
            [],
        );

        const patchDraftCustomRange = useCallback((patch) => {
            setDraftCustomRange((prev) => {
                const next = { ...prev, ...patch };
                draftCustomRangeRef.current = next;
                return next;
            });
        }, []);

        const handlePopoverVisibleChange = useCallback(
            (visible) => {
                setPopoverVisible(visible);
                if (visible) {
                    skipCommitOnNextCloseRef.current = false;
                    const nextDraft = clampCustomRangeToSendAt(
                        cloneCustomRangeState(customRange),
                        campaignSendAt,
                    );
                    draftCustomRangeRef.current = nextDraft;
                    setDraftCustomRange(nextDraft);
                }
            },
            [campaignSendAt, customRange],
        );

        const dismissConfig = useMemo(
            () => ({
                outsidePress: (event) => {
                    const { target } = event;
                    if (target instanceof Element) {
                        if (target.closest('.flatpickr-calendar')) {
                            return false;
                        }
                        if (target.closest('[role="listbox"]')) {
                            return false;
                        }
                    }
                    return true;
                },
            }),
            [],
        );

        const sendCalendarMinDate = useMemo(
            () =>
                campaignSendAt && !Number.isNaN(campaignSendAt.getTime())
                    ? startOfLocalDay(campaignSendAt)
                    : null,
            [campaignSendAt],
        );

        const endDateMinDate = useMemo(
            () =>
                maxLocalCalendarDayDate(draftCustomRange.startDate, sendCalendarMinDate) ??
                undefined,
            [draftCustomRange.startDate, sendCalendarMinDate],
        );

        const startTimeMinTime = useMemo(() => {
            if (
                !campaignSendAt ||
                Number.isNaN(campaignSendAt.getTime()) ||
                !draftCustomRange.startDate
            ) {
                return undefined;
            }
            if (!isSameCalendarDay(draftCustomRange.startDate, campaignSendAt)) {
                return undefined;
            }
            return formatHhMmSsForTimePicker(campaignSendAt);
        }, [campaignSendAt, draftCustomRange.startDate]);

        const endTimeMinTime = useMemo(() => {
            if (
                !draftCustomRange.startDate ||
                !draftCustomRange.endDate ||
                !draftCustomRange.startTime ||
                !isSameCalendarDay(draftCustomRange.startDate, draftCustomRange.endDate)
            ) {
                return undefined;
            }
            return formatHhMmSsForTimePicker(draftCustomRange.startTime);
        }, [draftCustomRange.endDate, draftCustomRange.startDate, draftCustomRange.startTime]);

        const handleEndDateChange = useCallback(
            (next) => {
                if (
                    next &&
                    draftCustomRange.startDate &&
                    draftCustomRange.startTime &&
                    draftCustomRange.endTime &&
                    isSameCalendarDay(draftCustomRange.startDate, next)
                ) {
                    const fromMs = combineLocalDateAndTime(
                        draftCustomRange.startDate,
                        draftCustomRange.startTime,
                    )?.getTime();
                    const toMs = combineLocalDateAndTime(next, draftCustomRange.endTime)?.getTime();
                    if (fromMs != null && toMs != null && fromMs > toMs) {
                        patchDraftCustomRange({ endDate: next, endTime: null });
                        return;
                    }
                }
                patchDraftCustomRange({ endDate: next });
            },
            [
                draftCustomRange.endTime,
                draftCustomRange.startDate,
                draftCustomRange.startTime,
                patchDraftCustomRange,
            ],
        );

        const handleStartTimeChange = useCallback(
            (nextDisplay) => {
                const next = fromTimePickerDisplayValue(nextDisplay);
                if (
                    next &&
                    draftCustomRange.endTime &&
                    draftCustomRange.startDate &&
                    draftCustomRange.endDate &&
                    isSameCalendarDay(draftCustomRange.startDate, draftCustomRange.endDate)
                ) {
                    const fromMs = combineLocalDateAndTime(
                        draftCustomRange.startDate,
                        next,
                    )?.getTime();
                    const toMs = combineLocalDateAndTime(
                        draftCustomRange.endDate,
                        draftCustomRange.endTime,
                    )?.getTime();
                    if (fromMs != null && toMs != null && fromMs > toMs) {
                        patchDraftCustomRange({ startTime: next, endTime: null });
                        return;
                    }
                }
                patchDraftCustomRange({ startTime: next });
            },
            [
                draftCustomRange.endDate,
                draftCustomRange.endTime,
                draftCustomRange.startDate,
                patchDraftCustomRange,
            ],
        );

        const popoverContent = useMemo(
            () => (
                <div className="fm-performance-over-time__custom-popover">
                    <div className="fm-performance-over-time__custom-section">
                        <Text type="caption" color="neutral700">
                            {getLiteral('common_date')}
                        </Text>
                        <div className="fm-performance-over-time__custom-row">
                            <DatePicker
                                className="fm-performance-over-time__custom-input"
                                isFullWidth
                                minDate={sendCalendarMinDate ?? undefined}
                                size="small"
                                value={draftCustomRange.startDate}
                                onChange={(next) => {
                                    if (next && draftCustomRange.endDate) {
                                        const startDay = new Date(next);
                                        startDay.setHours(0, 0, 0, 0);
                                        const endDay = new Date(draftCustomRange.endDate);
                                        endDay.setHours(0, 0, 0, 0);
                                        if (endDay < startDay) {
                                            patchDraftCustomRange({
                                                startDate: next,
                                                endDate: null,
                                            });
                                            return;
                                        }
                                    }
                                    if (next && draftCustomRange.endDate && sendCalendarMinDate) {
                                        const minEndDay = maxLocalCalendarDayDate(
                                            next,
                                            sendCalendarMinDate,
                                        );
                                        const endNorm = startOfLocalDay(draftCustomRange.endDate);
                                        if (
                                            minEndDay &&
                                            endNorm &&
                                            endNorm.getTime() < minEndDay.getTime()
                                        ) {
                                            patchDraftCustomRange({
                                                startDate: next,
                                                endDate: null,
                                                endTime: null,
                                            });
                                            return;
                                        }
                                    }
                                    patchDraftCustomRange({ startDate: next });
                                }}
                                lang={lang}
                                calendarButtonLabel={getLiteral('label_today')}
                                placeholder={userDateFormat}
                                placeholderReadOnly={userDateFormat}
                                inputMaskFormat={userDateFormat}
                                formatDate={(dateVal) => formatDate(dateVal, 'L')}
                            />
                            <span className="fm-performance-over-time__custom-sep">
                                {getLiteral('label_to')}
                            </span>
                            <DatePicker
                                className="fm-performance-over-time__custom-input"
                                isFullWidth
                                minDate={endDateMinDate}
                                value={draftCustomRange.endDate}
                                onChange={handleEndDateChange}
                                lang={lang}
                                calendarButtonLabel={getLiteral('label_today')}
                                placeholder={userDateFormat}
                                placeholderReadOnly={userDateFormat}
                                inputMaskFormat={userDateFormat}
                                formatDate={(dateVal) => formatDate(dateVal, 'L')}
                            />
                        </div>
                    </div>
                    <div className="fm-performance-over-time__custom-section">
                        <Text type="caption" color="neutral700">
                            {getLiteral('label_time')}
                        </Text>
                        <div className="fm-performance-over-time__custom-row">
                            <TimePicker
                                className="fm-performance-over-time__custom-input"
                                isFullWidth
                                minTime={startTimeMinTime}
                                value={toTimePickerDisplayValue(draftCustomRange.startTime)}
                                onChange={handleStartTimeChange}
                                placeholder="hh:mm"
                                placeholderReadOnly="hh:mm"
                                interval={TIME_INTERVAL_MINUTES}
                            />
                            <span className="fm-performance-over-time__custom-sep">
                                {getLiteral('label_to')}
                            </span>
                            <TimePicker
                                className="fm-performance-over-time__custom-input"
                                isFullWidth
                                minTime={endTimeMinTime}
                                value={toTimePickerDisplayValue(draftCustomRange.endTime)}
                                onChange={(nextDisplay) =>
                                    patchDraftCustomRange({
                                        endTime: fromTimePickerDisplayValue(nextDisplay),
                                    })
                                }
                                placeholder="hh:mm"
                                placeholderReadOnly="hh:mm"
                                interval={TIME_INTERVAL_MINUTES}
                            />
                        </div>
                    </div>
                </div>
            ),
            [
                draftCustomRange.endDate,
                draftCustomRange.endTime,
                draftCustomRange.startDate,
                draftCustomRange.startTime,
                endDateMinDate,
                endTimeMinTime,
                handleEndDateChange,
                handleStartTimeChange,
                lang,
                patchDraftCustomRange,
                sendCalendarMinDate,
                startTimeMinTime,
                userDateFormat,
            ],
        );

        const isCustomizeSelected = preset === presets.CUSTOM;

        const customPresetBadgeLabel = useMemo(() => {
            if (!isCustomizeSelected) {
                return getLiteral('cfm_label_custom');
            }
            return formatCustomRangeBadgeLabel(customRange) ?? getLiteral('cfm_label_custom');
        }, [customRange, isCustomizeSelected]);

        return (
            <Popover
                content={popoverContent}
                dismissConfig={dismissConfig}
                onVisibleChange={handlePopoverVisibleChange}
                overlayInnerStyle={CUSTOM_RANGE_POPOVER_INNER_STYLE}
                overlayStyle={CUSTOM_RANGE_POPOVER_OVERLAY_STYLE}
                placement="bottom-start"
                trigger={['click']}
                visible={popoverVisible}
            >
                <button
                    type="button"
                    className={
                        isCustomizeSelected
                            ? 'fm-performance-over-time__preset-btn fm-performance-over-time__preset-btn--selected'
                            : 'fm-performance-over-time__preset-btn'
                    }
                    aria-pressed={isCustomizeSelected}
                >
                    <span className="fm-performance-over-time__preset-btn-inner">
                        <Icon
                            name="calendarViewWorkDay"
                            size="smallMedium"
                            color={
                                isCustomizeSelected
                                    ? getVar(theme.colors.primary.white)
                                    : getVar(theme.colors.textLight.secondary)
                            }
                        />
                        <Text
                            type="badges"
                            color={
                                isCustomizeSelected
                                    ? getVar(theme.colors.primary.white)
                                    : getVar(theme.colors.textLight.secondary)
                            }
                        >
                            {customPresetBadgeLabel}
                        </Text>
                    </span>
                </button>
            </Popover>
        );
    }),
);

PerformanceOverTimeCustomRangePopover.displayName = 'PerformanceOverTimeCustomRangePopover';

const customRangePropType = PropTypes.shape({
    endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
    endTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
    startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
    startTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
});

PerformanceOverTimeCustomRangePopover.propTypes = {
    campaignSendAt: PropTypes.instanceOf(Date),
    customRange: customRangePropType.isRequired,
    onApplyCustomRange: PropTypes.func.isRequired,
    preset: PropTypes.string.isRequired,
    presets: PropTypes.object.isRequired,
};

export default PerformanceOverTimeCustomRangePopover;
